<template>
  <v-container>
    <v-row text-xs-center wrap>
      <v-col :cols="12">
        <v-card>
          <v-card-title>Email Status: {{ status }}</v-card-title>
          <v-card-text>
            <span v-if="message">{{ message }}</span>
            <v-expansion-panels>
              <v-expansion-panel>
                <v-expansion-panel-header>View Email (subject, body, filters)</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-container grid-list-md>
                    <v-row>
                      <v-col cols="12">
                        <v-text-field v-model="subject" readonly label="Subject"></v-text-field>
                      </v-col>
                      <v-col cols="12">
                        <label>Email Message</label>
                        <div v-html="body" style="border:1px solid #DCDCDC;padding:2em;"></div>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-text-field :value="filters.name" label="Name Filter" readonly clearable outlined></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select :value="filters.sections" :items="filters.sections" label="Section Filter" readonly multiple chips small-chips deletable-chips outlined></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-select :value="filters.sectionStatus" :items="filters.sectionStatus" label="Section Status" hint="Combines with Section Filter" readonly persistent-hint multiple chips small-chips deletable-chips outlined></v-select>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-subheader style="padding:0">Sections Completed Range</v-subheader>
                        <v-text-field :value="filters.sectionsCompleted.min" label="Minimum" type="number" min="0" :max="sections.length" step="1" readonly outlined clearable></v-text-field>
                        <v-text-field :value="filters.sectionsCompleted.max" label="Maximum" type="number" min="0" :max="sections.length" step="1" outlined clearable></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-subheader style="padding:0">Slides Completed Range</v-subheader>
                        <v-text-field :value="filters.slidesCompleted.min" label="Minimum" type="number" min="0" step="1" readonly outlined clearable></v-text-field>
                        <v-text-field :value="filters.slidesCompleted.max" label="Maximum" type="number" min="0" :max="slideCount" step="1" readonly outlined clearable></v-text-field>
                      </v-col>
                      <v-col cols="12" md="4">
                        <v-subheader style="padding:0">Last Updated Date Range</v-subheader>
                        <date-picker :value="filters.lastUpdate.min" :maxDate="lastUpdateMax === '' ? today : lastUpdateMax" :clear-button="true" label="Minimum Date" readonly></date-picker>
                        <date-picker :value="filters.lastUpdate.max" :maxDate="today" :minDate="lastUpdateMin" :clear-button="true" label="Maximum Date" readonly></date-picker>
                      </v-col>
                      <v-col cols="12">
                        <v-switch :value="filters.excludeTests" :label="(excludeTests ? 'Exclude' : 'Only Include') + ' Staff Test Accounts'" readonly></v-switch>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </v-card-text>
          <v-data-table :items="data" :headers="headers">
            <template v-slot:item._emails="{ item }">
              {{ item._emails.join('; ') }}
            </template>
            <template v-slot:item._status="{ item }">
              {{ item._status || 'Pending' }}
            </template>
            <template v-slot:item._preview="{ item }">
              <v-btn icon @click="showPreview(item)">
                <v-icon>fas fa-eye</v-icon>
              </v-btn>
            </template>
          </v-data-table>
          <v-card-actions>
            <v-btn text to="/admin/tracker/email-log">Return to email log</v-btn>
            <v-btn v-if="status === 'Queued' || status === 'Scheduled'" text @click="cancelJob">Cancel Job</v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-dialog v-model="previewDialog" width="500">
      <v-card>
        <v-card-title v-html="previewSubject"></v-card-title>
        <v-card-text v-html="previewBody"></v-card-text>
        <v-card-actions>
          <v-btn text @click="previewDialog = false">Close</v-btn>
          <v-btn text @click="sendOne">{{ sendText }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>
<script>
import { computed, onMounted, ref } from '@vue/composition-api'
export default {
  setup (props, { root }) {
    const id = computed(() => root.$route.params.id)
    const createdBy = ref('')
    const status = ref('')
    const runDate = new Date()
    const subject = ref('')
    const body = ref('')
    const sent = ref(0)
    const failed = ref(0)
    const recipientCount = ref(0)
    const filters = ref({
      name: '',
      sections: [],
      sectionStatus: [],
      sectionsCompleted: { min: null, max: null },
      slidesCompleted: { min: null, max: null },
      lastUpdate: { min: null, max: null },
      excludeTests: true
    })
    const data = ref([])

    function formatDate (dt) {
      return dt.toISOString()
    }

    const headers = computed(() => {
      let arr = [
        { text: 'Emails', value: '_emails' },
        { text: 'Status', value: '_status' }
      ]
      for (let l in data.value[0]) {
        if (l === '_pidm' || l === '_emails' || l === '_status') continue
        arr.push({ text: l, value: l })
      }
      arr.push({ text: 'Preview', value: '_preview' })
      return arr
    })
    const message = computed(() => {
      if (status.value === 'Scheduled') return 'This email is scheduled to be sent on ' + formatDate(runDate)
      if (status.value === 'Queued') return 'This email is in the queue and should start sending soon'
      if (status.value === 'In Progress') return 'This email is currently in the process of being sent; refer to the table at the bottom for details'
      if (status.value === 'Complete') return 'This email has been sent'
      return ''
    })

    function cancelJob () {
      if (confirm('Are you sure you want to cancel the selected job? This cannot be undone.')) {
        root.$feathers.service('orientation/email-log').patch(id.value, { status: 'Cancelled' }).then((data) => {
          if (data.status === 'Cancelled') {
            this.$store.dispatch('snackbar', {
              color: 'success',
              text: 'Email job cancelled',
              timeout: 3000,
              active: true
            })
          }
          status.value = data.status
        })
      }
    }
    function showPreview (item) {
      previewData.value = item
      previewDialog.value = true
    }

    const previewDialog = ref(false)
    const previewData = ref({})
    function mergePreview (data, text) {
      if (!('_pidm' in data)) return ''
      // Determine which merge fields are actually in use
      let matches = text.match(/<code>[\w ]*<\/code>/ig) || []
      // Parse the body and extract all of the <code></code> blocks. These are the placeholder fields that need to be replaced in the preview window with the actual data
      for (let i = 0; i < matches.length; i++) {
        let match = matches[i]
        // Extract the text of the label (i.e. remove <code> from the beginning and </code> from the end, so skip first 6 characters and last 7, which makes a total reduction in length of 13)
        let field = matches[i].substr(6, matches[i].length - 13)
        // Get the text which will replace the source placeholder field; if the matching field does not exist in the data, then we just remove the placeholder text
        let text = (field in data ? data[field] : '')
        text = text.replace(match, text)
      }
      return text
    }
    const previewSubject = computed(() => {
      return mergePreview(previewData.value, subject.value)
    })
    const previewBody = computed(() => {
      return mergePreview(previewData.value, body.value)
    })
    const sendText = computed(() => {
      if (!('_pidm' in previewData.value)) return ''
      if (!('_status' in previewData.value) || previewData.value._status === 'Pending') return 'Send email'
      else return 'Send email again'
    })
    function sendOne () {
      root.$feathers.service('system/email').create({
        from: 'studentdevelopment@covenant.edu',
        to: [ previewData.value.email, previewData.value.homeEmail ],
        subject: previewSubject.value,
        html: previewBody.value
      }).catch((text) => {
        root.$store.dispatch('snackbar', { active: true, color: 'error', text, timeout: 10000 })
      }).then(() => {
        root.$store.dispatch('snackbar', { active: true, color: 'success', text: 'Email sent successfully', timeout: 6000 })
      })
    }

    onMounted(() => {
      if (!root.$store.getters.hasRole('Employee')) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the admin system.' })
        root.$router.push('/')
        return
      } else if (!root.$store.getters.isAdmin) {
        root.$store.dispatch('snackbar', { active: true, color: 'error', timeout: 6000, text: 'You do not have access to the email tracker.' })
        root.$router.push('/admin')
        return
      }
      root.$feathers.service('orientation/email-log').get(id.value).then((logData) => {
        runDate.value = new Date(logData.runDate)
        createdBy.value = logData.createdBy
        status.value = logData.status
        subject.value = logData.subject
        body.value = logData.body
        sent.value = logData.sent
        failed.value = logData.failed
        recipientCount.value = logData.recipientCount
        filters.value.name = logData.filters.name
        filters.value.sections = logData.filters.sections
        filters.value.sectionStatus = logData.filters.sectionStatus
        filters.value.sectionsCompleted = logData.filters.sectionsCompleted
        filters.value.slidesCompleted = logData.filters.slidesCompleted
        filters.value.lastUpdate = logData.filters.lastUpdate
        filters.value.excludeTests = logData.filters.excludeTests
        data.value = logData.data
      })
      root.$feathers.service('orientation/email-log').on('patched', (log) => {
        if (log._id !== id.value) return
        let logRunDate = new Date(log.runDate)
        if (runDate.value !== logRunDate.value) runDate.value = logRunDate
        if (data.value.length === 0) data.value = log.data
        else if (log.data.length > 0) {
          log.data.forEach((row, index) => {
            while (data.value.length <= index) data.value.push({ _pidm: '', _status: '', _emails: [] })
            data.value.splice(index, row, 1)
          })
        }
        for (let l in log) {
          if (l !== 'runDate' && l !== 'data' && l in this && this[l] !== log[l]) {
            this[l] = log[l]
          }
        }
      })
    })

    return {
      id,
      createdBy,
      status,
      subject,
      body,
      sent,
      failed,
      recipientCount,
      filters,
      data,
      headers,
      message,
      cancelJob,
      showPreview,
      previewDialog,
      previewData,
      previewSubject,
      previewBody,
      sendText,
      sendOne
    }
  }
}
</script>
